import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    IconButton,
    MenuItem,
    TextField,
    Typography
} from "@mui/material"
import React, { useEffect, useState } from "react"
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
  addWorkerAccount,
  checkIfWorkerWithEmailExists,
  checkIfWorkerWithPTSExists,
  getAllCompetencies,
  getAllExperiences,
  getAllWorkerIdentificationTypes,
  GetAllWorkRole,
  getTickets,
  getUsersWithSamePhoneNr,
} from "../../../api/apiCall";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from "../../../auth/auth";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useFieldArray } from "react-hook-form";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

type FormValues = {
    nationalInsuranceId: string;
    name: string;
    surname: string;
    pts: string;
    phoneNumber: string;
    email: string;
    postCode: string;
    location: string;
    suffix: string;
    notes: string;
    driver: boolean;
    competencies: any[];
    experiences: string[];
    tickets: string[];
    workRoles: string[];
    dateOfBirth: string;
    nationality: string;
    gender: string;
    hasDisability: boolean;
    disabilityDetails: string;
    hasCriminalRecord: boolean;
    criminalRecordSpent: string;
    workerIdentifications: {
        identificationTypeId: string;
        number: string;
    }[];
}

const CreateWorker = () => {
    const {state} = useAuth();
    const [competencies, setCompetencies] = useState<any>([]);
    const [experiences, setExperiences] = useState<any>([]);
    const [tickets, setTickets] = useState<any>([]);
    const [buttonLoading, setButtonLoading] = useState(false);
    const [selectedCompetencies, setSelectedCompetencies] = useState<string[]>([]);
    const [selectedExperiences, setSelecetedExperiences] = useState<string[]>([]);
    const [selectedTickets, setSelectedTickets] = useState<string[]>([]);
    const [workRoles, setWorkRoles] = useState<any>([]);
    const [identifications, setIdentifications] = useState<any>([]);
    const [selectedWorkRoles, setSelectedWorkRoles] = useState<string[]>([]);
    const [showDisabilitySpecify, setShowDisabilitySpecify] = useState(false);
    const [showCriminalRecordSpent, setShowCriminalRecordSpent] = useState(false);
    const {
      register,
      handleSubmit,
      control,
      formState: { errors },
      setValue,
      reset,
      setError,
      clearErrors,
      watch,
    } = useForm<FormValues>();

    const { fields, append, remove } = useFieldArray({
        control,
        name: "workerIdentifications",
    });

    const hasDisabilityValue: any = watch("hasDisability");
    const criminalRecordValue: any = watch("hasCriminalRecord");

    useEffect(() => {
        setShowDisabilitySpecify(hasDisabilityValue === "Yes" ? true : false);
    }, [hasDisabilityValue]);

    useEffect(() => {
        setShowCriminalRecordSpent(criminalRecordValue === "Yes" ? true : false);
    }, [criminalRecordValue]);

    const getAllIdentifications = async () => {
        const response = await getAllWorkerIdentificationTypes(state.user?.token);
        setIdentifications(response.data);
    }

    const createWorker = async (data: any) => {
        try {
            const ptsExists = await checkIfWorkerWithPTSExists(state.user?.token, data.pts);
            if(ptsExists) {
                setError("pts", {
                  message: "This PTS Number is already assigned to another worker",
                });
                return;
            }
            const emailExists = await checkIfWorkerWithEmailExists(state.user?.token, data.email);
            if(emailExists) {
                setError("email", {
                  message: "This email address is already assigned to another worker",
                });
                return;

            }

            setButtonLoading(true);
            data.hasCriminalRecord = data.hasCriminalRecord === "Yes" ? true : false;
            data.hasDisability = data.hasDisability === "Yes" ? true : false;
            if(data.hasCriminalRecord === true) {
                data.criminalRecordSpent = data.criminalRecordSpent === "Yes" ? true : false;
            }
            data.competencies = selectedCompetencies;
            data.experiences = selectedExperiences;
            data.tickets = selectedTickets;
            data.workRoles = selectedWorkRoles;

            const response = await addWorkerAccount(state.user?.token, data);
            if(response) {
                setTimeout(() => {
                    setButtonLoading(false);
                    toast.success('Worker added successfully');
                    reset({
                        nationalInsuranceId: '',
                        name: '',
                        surname: '',
                        pts: 'N/A',
                        phoneNumber: '',
                        email: 'N/A',
                        postCode: 'N/A',
                        location: 'N/A',
                        suffix: 'N/A',
                        notes: '',
                        driver: false,
                        competencies: [],
                        experiences: [],
                        tickets: [],
                        workRoles: [],
                        dateOfBirth: '',
                        nationality: '',
                        gender: '',
                        hasDisability: false,
                        disabilityDetails: '',
                        hasCriminalRecord: false,
                        criminalRecordSpent: '',
                    });
                }, 500);
            }
        } catch (error) {
            setButtonLoading(false);
            toast.error('An error occurred while adding the worker. Please try again.');
        }
        setButtonLoading(false);
    };

    const workerWithPTSExists = async (ptsNumber: any) => {
        const response = await checkIfWorkerWithPTSExists(state.user?.token, ptsNumber);
        if(response) {
            setError("pts", {
              message: "This PTS number is already assigned to another worker",
            });
        } else {
            clearErrors("pts");
        }
    };

     const workerWithEmailExists = async (email: any) => {
        const response = await checkIfWorkerWithEmailExists(state.user?.token, email);
        if(response) {
            setError("email", {
              message: "This email address is already assigned to another worker",
            });
        } else {
            clearErrors("email");
        }
    };

    const workerWithPhoneNumberExists = async (phoneNumber: any) => {
        const response = await getUsersWithSamePhoneNr(state.user?.token, phoneNumber);
        if(response) {
            setError("phoneNumber", {
              message: `This Phone Number is assigned to ${response} workers`,
            });
        } else {
            clearErrors("phoneNumber");
        }
    };

    useEffect(() => {
        getAllCompetencies(state.user?.token).then(setCompetencies);
        getAllExperiences(state.user?.token).then(setExperiences);
        getTickets(state.user?.token).then(setTickets);
        GetAllWorkRole(state.user?.token).then(setWorkRoles);
        getAllIdentifications();
    }, []);

    return (
        <Box bgcolor={"#f2f5fc"} >
            <Box
                sx={{
                    maxWidth: 1000,
                    margin: "auto",
                    padding: 5,
                    backgroundColor: "#fff"
                }}
            >
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Create New Worker
                </Typography>
                <Box sx={{borderBottom: 1, borderColor: "divider", mb: 2}} />
                <Box display="grid" gridTemplateColumns={{xs: "1fr", md: "1fr 1fr"}} gap={3} mt={7}>
                    <TextField
                        label="National Insurance Number"
                        placeholder="National Insurance Number"
                        variant="outlined"
                        size="small"
                        fullWidth
                        {...register("nationalInsuranceId", {
                            required: "NIN is required",
                            pattern: {
                                value: /^(?!BG)(?!GB)(?!NK)(?!KN)(?!TN)(?!NT)(?!ZZ)(?:[A-CEGHJ-PR-TW-Z][A-CEGHJ-NPR-TW-Z])(?:\s*\d\s*){6}([A-D]|\s)$/,
                                message: "Please enter a valid NIN"
                            },
                            maxLength: {
                                value: 9,
                                message: "National Insurance Number must be at most 9 characters"
                            }
                        })}
                        onBlur={(e) => {
                            const trimmedValue = e.target.value.trim();
                            setValue("nationalInsuranceId", trimmedValue);
                        }}
                        error={!!errors.nationalInsuranceId}
                        helperText={errors.nationalInsuranceId?.message}
                        required
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        {...register("competencies")}
                        options={competencies}
                        disableCloseOnSelect
                        onChange={(_, newValue) => {
                            const competenciesSelected = newValue.map(item => item.id);
                            setSelectedCompetencies(competenciesSelected)
                        }}
                        getOptionLabel={(option: any) => option.skillName}
                        renderOption={(props, option, { selected }) => {
                            const { key, ...optionProps }: any = props;
                            return (
                                <li key={key} {...optionProps}>
                                    <Checkbox
                                        icon={icon}
                                        checkedIcon={checkedIcon}
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                    />
                                        {option.skillName}
                                </li>
                            );
                        }}
                        style={{ width: 500 }}
                        renderInput={(params) => (
                            <TextField {...params} label="Competencies" placeholder="Select Competencies" />
                        )}
                    />
                    <TextField
                        label="Name"
                        placeholder="Name"
                        variant="outlined"
                        size="small"
                        fullWidth
                        {...register("name", {
                            required: "Name is required",
                            minLength: {
                                value: 3,
                                message: "Name must be at least 3 characters"
                            },
                            maxLength: {
                                value: 20,
                                message: "Name must be at most 20 characters"
                            }
                        })}
                        onBlur={(e) => {
                            const trimmedValue = e.target.value.trim();
                            setValue("name", trimmedValue);
                        }}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        required
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        {...register("tickets")}
                        options={tickets}
                        disableCloseOnSelect
                        onChange={(_, newValue) => {
                            const ticketsSelected = newValue.map(item => item.id);
                            setSelectedTickets(ticketsSelected)
                        }}
                        getOptionLabel={(option: any) => option.ticketName}
                        renderOption={(props, option, { selected }) => {
                            const { key, ...optionProps }: any = props;
                            return (
                                <li key={key} {...optionProps}>
                                    <Checkbox
                                        icon={icon}
                                        checkedIcon={checkedIcon}
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                    />
                                        {option.ticketName}
                                </li>
                            );
                        }}
                        style={{width: 500}}
                        renderInput={(params) => (
                            <TextField {...params} label="Tickets" placeholder="Select Tickets" />
                        )}
                    />
                    <TextField
                        label="Surname"
                        size="small"
                        variant="outlined"
                        fullWidth
                        error={!!errors.surname}
                        {...register("surname", {
                            required: "Surname is required",
                            minLength: {
                                value: 3,
                                message: "Surname must be at least 3 characters"
                            },
                            maxLength: {
                                value: 20,
                                message: "Surname must be at most 20 characters"
                            }
                        })}
                        helperText={errors.surname?.message}
                        onBlur={(e) => {
                            const trimmedValue = e.target.value.trim();
                            setValue("surname", trimmedValue);
                        }}
                        required
                    />
                     <Controller
                      name="workRoles"
                      control={control}
                      rules={{ required: "At least one work role must be selected" }}
                      render={({ field }) => (
                        <Autocomplete
                          {...field}
                          multiple
                          size="small"
                          options={workRoles}
                          disableCloseOnSelect
                          onChange={(_, newValue) => {
                            field.onChange(newValue);
                            const workRolesSelected = newValue.map(item => item.id);
                            setSelectedWorkRoles(workRolesSelected);
                          }}
                          value={field.value || []}
                          getOptionLabel={(option: any) => option.workRoleName}
                          renderOption={(props, option, { selected }) => {
                            const { key, ...optionProps }: any = props;
                            return (
                              <li key={key} {...optionProps}>
                                <Checkbox
                                  icon={icon}
                                  checkedIcon={checkedIcon}
                                  style={{ marginRight: 8 }}
                                  checked={selected}
                                />
                                {option.workRoleName}
                              </li>
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Work Roles"
                              error={!!errors.workRoles}
                              helperText={errors.workRoles?.message}
                              required
                            />
                          )}
                          sx={{
                            '& .MuiAutocomplete-inputRoot': {
                              flexWrap: 'nowrap',
                              overflowX: 'auto',
                              scrollbarWidth: 'thin',
                              '&::-webkit-scrollbar': {
                                height: '9px',
                              },
                            },
                            '& .MuiChip-root': {
                              marginRight: '4px',
                              maxWidth: '150px',
                              overflow: 'hidden',
                            },
                          }}
                        />
                      )}
                    />
                    <Box display="flex" alignItems="center" gap={1}>
                        <TextField
                            label="PTS"
                            size="small"
                            variant="outlined"
                            fullWidth
                            {...register("pts", {
                                required: "PTS is required"
                            })}
                            error={!!errors.pts}
                            helperText={errors.pts?.message}
                            onBlur={(e) => {
                                const trimmedValue = e.target.value.trim();
                                setValue("pts", trimmedValue);
                                workerWithPTSExists(trimmedValue)
                            }}
                            required
                        />
                        <IconButton
                            onClick={() =>
                                append({
                                    identificationTypeId: "",
                                    number: "",
                                })
                            }
                            size="small"
                        >
                            <AddIcon />
                        </IconButton>
                    </Box>
                   <TextField
                        label="Post Code"
                        variant="outlined"
                        fullWidth
                        size="small"
                        error={!!errors.postCode}
                        helperText={errors.postCode?.message}
                        {...register("postCode", {
                            required: "Post Code is required",
                            pattern: {
                                value: /^(N\/A|[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2})$/i,
                                message: "Please enter a valid UK postcode or N/A"
                            }
                        })}
                        onBlur={(e) => {
                            const value = e.target.value.toUpperCase();
                            setValue("postCode", value);
                        }}
                        required
                    />
                    <TextField
                        label="Phone Number"
                        variant="outlined"
                        fullWidth
                        size="small"
                        error={!!errors.phoneNumber}
                        {...register("phoneNumber", {
                            required: "Phone Number is required",
                            maxLength: {
                                value: 20,
                                message: "Phone Number must be at most 20 characters"
                            }
                        })}
                        helperText={errors.phoneNumber?.message}
                        onBlur={(e) => {
                            const trimmedValue = e.target.value.trim();
                            setValue("phoneNumber", trimmedValue);
                            workerWithPhoneNumberExists(trimmedValue)
                        }}
                        required
                    />
                    <TextField
                        label="Location"
                        variant="outlined"
                        fullWidth
                        size="small"
                        {...register("location", {
                            required: "Location is required"
                        })}
                        error={!!errors.location}
                        helperText={errors.location?.message}
                        required
                    />
                    <TextField
                        label="Email"
                        variant="outlined"
                        fullWidth
                        size="small"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        {...register("email", {
                            required: "Email is required",
                            pattern: {
                                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                message: "Please enter a valid email address"
                            }
                        })}
                        onBlur={(e) => {
                            const trimmedValue = e.target.value.trim();
                            setValue("email", trimmedValue);
                            workerWithEmailExists(trimmedValue)
                        }}
                        required
                    />
                    <Controller
                        name="nationality"
                        control={control}
                        rules={{ required: "Nationality is required" }}
                        render={({ field }) => (
                            <TextField
                              {...field}
                              select
                              label="Nationality"
                              fullWidth
                              size="small"
                              error={!!errors.nationality}
                              helperText={errors.nationality?.message}
                              required
                            >
                                <MenuItem value="">Select Nationality</MenuItem>
                                <MenuItem value="Albanian">Albanian</MenuItem>
                                <MenuItem value="British">British</MenuItem>
                                <MenuItem value="Cameroonian">Cameroonian</MenuItem>
                                <MenuItem value="Irish">Irish</MenuItem>
                                <MenuItem value="Ukrainian">Ukrainian</MenuItem>
                                <MenuItem value="French">French</MenuItem>
                                <MenuItem value="German">German</MenuItem>
                                <MenuItem value="Welsh">Welsh</MenuItem>
                                <MenuItem value="Fijian">Fijian</MenuItem>
                                <MenuItem value="Lithuanian">Lithuanian</MenuItem>
                                <MenuItem value="Spanish">Spanish</MenuItem>
                                <MenuItem value="Italian">Italian</MenuItem>
                                <MenuItem value="Sri Lankan">Sri Lankan</MenuItem>
                                <MenuItem value="Norwegian">Norwegian</MenuItem>
                                <MenuItem value="Malawian">Malawian</MenuItem>
                                <MenuItem value="Gambian">Gambian</MenuItem>
                                <MenuItem value="Polish">Polish</MenuItem>
                                <MenuItem value="Romanian">Romanian</MenuItem>
                                <MenuItem value="Indian">Indian</MenuItem>
                                <MenuItem value="Pakistani">Pakistani</MenuItem>
                                <MenuItem value="Bangladeshi">Bangladeshi</MenuItem>
                                <MenuItem value="Chinese">Chinese</MenuItem>
                                <MenuItem value="Nigerian">Nigerian</MenuItem>
                                <MenuItem value="Ghanaian">Ghanaian</MenuItem>
                                <MenuItem value="South African">South African</MenuItem>
                                <MenuItem value="American">American</MenuItem>
                                <MenuItem value="Canadian">Canadian</MenuItem>
                                <MenuItem value="Australian">Australian</MenuItem>
                            </TextField>
                        )}
                    />
                     <TextField
                        label="Date of Birth"
                        variant="outlined"
                        required
                        fullWidth
                        size="small"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.dateOfBirth}
                        helperText={errors.dateOfBirth?.message}
                        {...register("dateOfBirth", {
                            required: "Date of Birth is required"
                        })}
                    />
                    <TextField
                        select
                        label="Gender"
                        variant="outlined"
                        fullWidth
                        size="small"
                        {...register("gender")}
                    >
                        <MenuItem value="">Select Gender</MenuItem>
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                        <MenuItem value="Rather not say">Rather not say</MenuItem>
                    </TextField>
                    <TextField
                        select
                        label="Disability"
                        variant="outlined"
                        fullWidth
                        size="small"
                        {...register("hasDisability")}
                    >
                        <MenuItem value="">Select</MenuItem>
                        <MenuItem value="Yes">Yes</MenuItem>
                        <MenuItem value="No">No</MenuItem>
                    </TextField>
                    
                    <TextField
                        select
                        label="Criminal Record"
                        variant="outlined"
                        fullWidth
                        size="small"
                        {...register("hasCriminalRecord")}
                    >
                        <MenuItem value="">Select</MenuItem>
                        <MenuItem value="Yes">Yes</MenuItem>
                        <MenuItem value="No">No</MenuItem>
                    </TextField>
                    {showDisabilitySpecify && (
                        <TextField
                            label="Please specify disability"
                            variant="outlined"
                            fullWidth
                            size="small"
                            {...register("disabilityDetails")}
                        />
                    )}
                    {showCriminalRecordSpent && (
                        <TextField
                            select
                            label="Is criminal record spent?"
                            variant="outlined"
                            fullWidth
                            size="small"
                            {...register("criminalRecordSpent")}
                        >
                            <MenuItem value="">Select</MenuItem>
                            <MenuItem value="Yes">Yes</MenuItem>
                            <MenuItem value="No">No</MenuItem>
                        </TextField>
                    )}
                </Box>
                {fields.map((field, index) => (
                    <Box
                        key={field.id}
                        display="grid"
                        gridTemplateColumns="1fr 1fr auto"
                        gap={2}
                        mt={3}
                    >
                        <TextField
                            select
                            label="Type"
                            fullWidth
                            size="small"
                            {...register(`workerIdentifications.${index}.identificationTypeId`, {
                                required: "Type is required",
                            })}
                        >
                            {identifications.map((item: any) => (
                                <MenuItem key={item.id} value={item.id}>
                                    {item.name}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label="Value"
                            size="small"
                            {...register(`workerIdentifications.${index}.number`, {
                                required: "Value is required",
                            })}
                        />
                        <IconButton 
                            onClick={() => remove(index)} 
                            color="error"
                            size="small"
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                ))}
                <TextField
                    sx={{mt: 3}}
                    label="Notes"
                    variant="outlined"
                    fullWidth
                    size="small"
                    multiline
                    rows={4}
                    {...register("notes")}
                />
                <Typography style={{fontSize: 17}}>Driver:<Checkbox {...register("driver")} /></Typography>
                <Box display="flex" justifyContent="center" mt={3}>
                    <Button
                        variant="contained"
                        color="primary"
                        style={{boxShadow: "none", backgroundColor: "#156bc2", width: 160, color: "white"}}
                        onClick={handleSubmit(createWorker)}
                        disabled={!!errors.pts || buttonLoading}
                    >
                        {buttonLoading ? (
                            <CircularProgress size={25} style={{color: 'white'}} />
                        ) : ('Create Worker')}
                    </Button>
                </Box>
            </Box>
        </Box>
    )
};

export default CreateWorker;